import React, { useEffect, useState } from "react";
import { API_URL } from "../helpers/variables";
import {
  List,
  ListItem,
  Button,
  Stack,
  ListItemButton,
  ListItemText,
  Box,
  ListSubheader,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Backdrop,
  Dialog,
  Toolbar,
  Typography,
  DialogContent,
  Autocomplete,
  TextField,
  Chip,
  AppBar,
} from "@mui/material";
import Editor from "@monaco-editor/react";
import { Clear, Edit, Upload } from "@mui/icons-material";
import { useRouteLoaderData } from "react-router-dom";

const ConfigViewer = ({ selectedReport, reloadList }) => {
  const { edit } = useRouteLoaderData("authentication-context");

  const [configs, setConfigs] = useState([]);
  const [editedReport, setEditedReport] = useState(null);

  const [selectedConfigIndex, setSelectedConfigIndex] = useState(false);
  const [editedConfig, setEditedConfig] = useState();

  const selectedConfig = configs[selectedConfigIndex];

  const [loading, setLoading] = useState(false);
  const [addingNewConfig, setAddingNewConfig] = useState(false);

  useEffect(() => {
    getConfigs(selectedReport["configs"]);
  }, [selectedReport]);

  useEffect(() => {
    if (selectedConfig) setAddingNewConfig(false);
    const { _id, ...configWithoutId } = selectedConfig || {};
    setEditedConfig(JSON.stringify(configWithoutId || {}, null, 2));
  }, [selectedConfigIndex]);

  async function getConfigs(configIDS, defaultConfigIndex = 0) {
    setConfigs([]);
    setSelectedConfigIndex(false);
    setAddingNewConfig(false);

    if (configIDS.length === 0) {
      setAddingNewConfig(true);
      return;
    }

    setLoading(true);
    let res = await fetch(`${API_URL}/get_configs`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(configIDS),
    });
    let data = await res.json();
    setConfigs(data);
    setSelectedConfigIndex(defaultConfigIndex);
    setLoading(false);
  }

  async function updateConfig() {
    setLoading(true);
    try {
      let newConfig = JSON.parse(editedConfig);
      let res = await fetch(
        `${API_URL}/update_config?config_id=${selectedConfig["_id"]}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newConfig),
        }
      );
      if (res.status === 204) {
        getConfigs(selectedReport["configs"], selectedConfigIndex);
        reloadList();
      }
      if (res.status === 200)
        getConfigs(selectedReport["configs"], selectedConfigIndex);
    } catch (e) {
      alert(e);
    }
    setLoading(false);
  }

  async function addNewConfig() {
    setLoading(true);
    try {
      let newConfig = JSON.parse(editedConfig);
      let res = await fetch(
        `${API_URL}/add_config?report_id=${selectedReport["_id"]}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newConfig),
        }
      );
      if (res.status === 200) {
        let newConfigID = res.text();
        reloadList();
        getConfigs(
          [...selectedReport["configs"], newConfigID],
          selectedConfigIndex
        );
        setAddingNewConfig(false);
      }
    } catch (e) {
      alert(e);
    }
    setLoading(false);
  }

  async function updateReport() {
    setLoading(true);

    let { _id, name, alias_names, date_position } = editedReport;

    let res = await fetch(`${API_URL}/update_report?report_id=${_id}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name, alias_names, date_position }),
    });
    if (res.status === 204) {
      reloadList();
      setEditedReport();
    } else setLoading(false);
  }

  return (
    <>
      <Stack height={"100%"} border={1} borderColor={"divider"}>
        <AppBar position="static" elevation={0}>
          <Toolbar variant="dense">
            <Typography variant="h5" flexGrow={1}>
              {" "}
              {selectedReport["name"]}
            </Typography>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setEditedReport(selectedReport)}
            >
              Edit Report
            </Button>
          </Toolbar>
        </AppBar>
        <Stack direction={"row"} flexGrow={1}>
          <Box width={"75%"}>
            <Editor
              defaultLanguage="json"
              theme="vs-dark"
              value={editedConfig}
              onChange={(value) => setEditedConfig(value)}
              options={{
                minimap: { enabled: false },
                stickyScroll: { enabled: false },
              }}
              loading={<CircularProgress />}
            />
          </Box>

          <Stack width={"25%"}>
            <Tabs
              orientation="vertical"
              value={selectedConfigIndex}
              onChange={(_, newValue) => setSelectedConfigIndex(newValue)}
              sx={{ flexGrow: 1 }}
            >
              {configs.map((_, index) => (
                <Tab key={index} value={index} label={`Config ${index + 1}`} />
              ))}
              <Tab
                disabled={!edit}
                label={"Add new config"}
                onClick={() => setAddingNewConfig(true)}
              />
            </Tabs>

            <Box p={1}>
              {addingNewConfig ? (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={addNewConfig}
                  disabled={!edit}
                >
                  Add Config
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={updateConfig}
                  disabled={!edit}
                >
                  Update Config
                </Button>
              )}
            </Box>
          </Stack>
        </Stack>
      </Stack>

      <Dialog
        maxWidth="md"
        fullWidth
        open={editedReport}
        onClose={() => setEditedReport()}
      >
        <Toolbar variant="dense">
          <Typography variant="h6" flexGrow={1}>
            {editedReport?.name}
          </Typography>
          <IconButton color="inherit" onClick={() => setEditedReport()}>
            <Clear />
          </IconButton>
        </Toolbar>
        <DialogContent>
          {editedReport && (
            <Stack gap={3}>
              <TextField
                label="Name"
                value={editedReport["name"]}
                onChange={(e) =>
                  setEditedReport((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
              />

              <Autocomplete
                multiple
                freeSolo
                options={editedReport["alias_names"]}
                value={editedReport["alias_names"]}
                onChange={(e, v) =>
                  setEditedReport((prev) => ({ ...prev, alias_names: v }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Alias Names" />
                )}
              />

              <TextField
                label="Date Position"
                type="number"
                value={editedReport["date_position"]}
                onChange={(e) => {
                  let v = e.target.value;
                  setEditedReport((prev) => ({
                    ...prev,
                    date_position: v && v.trim() !== "" ? Number(v) : v,
                  }));
                }}
              />

              <Button disabled={!edit} onClick={updateReport}>
                Update
              </Button>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Backdrop open={loading} sx={{ zIndex: (t) => t.zIndex.tooltip }}>
        <CircularProgress />
      </Backdrop>
    </>
  );
};

export default ConfigViewer;
